import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { apiUrl } from '@app/app.constants';
import { PortalNotification } from '@app/core/interfaces/portal-notification';
import { PortalNotificationSendTestMail } from '@app/core/interfaces/portal-notification-send-test-mail';
import { PortalNotificationTranslation } from '@app/core/interfaces/portal-notification-translation';
import { PortalNotificationTranslationOrderUpdate } from '@app/core/interfaces/portal-notification-translation-order-update';
import { PortalNotificationTranslationUpdate } from '@app/core/interfaces/portal-notification-translation-update';
import { PortalNotificationUpdate } from '@app/core/interfaces/portal-notification-update';
import { PortalNotificationWithTranslations } from '@app/core/interfaces/portal-notification-with-translations';
import { PortalPublishedNotificationUpdate } from '@app/core/interfaces/portal-published-notification-update';
import { MyPortalNotification } from '@app/core/interfaces/portal/my-portal-notification';
import { PortalCopyNotification } from '@app/core/interfaces/portal/portal-copy-notification';
import { RenderedPortalNotification } from '@app/core/interfaces/rendered-portal-notification';
import { UserLevel } from '@app/core/models/user-level';
import { PortalDataService } from '@app/portal/services/portal-data.service';
import { Upload, UploadService } from '@app/shared/services/upload.service';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { formatISO } from 'date-fns';
import { Observable } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';
import { PortalNotificationEnhancedRecipient } from '../interfaces/portal-notification-enhanced-recipient';

@Injectable({
  providedIn: 'root',
})
export class PortalNotificationRepository {
  constructor(
    private http: HttpClient,
    private portalDataService: PortalDataService,
    private utils: Utils,
    private uploadService: UploadService
  ) {}

  copy(update: PortalCopyNotification): Observable<void> {
    return this.utils.routeBasedApiUrl(`/notifications/copy`).pipe(
      mergeMap((url) => this.http.post<void>(url, update)),
      wrapError('portal-notification-repository:copy')
    );
  }

  copyTranslations(notificationId: number, from: string, to: string): Observable<void> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/copyTranslations`).pipe(
      mergeMap((url) =>
        this.http.put<void>(url, {
          from,
          to,
        })
      ),
      wrapError('notification-repository:list-translations')
    );
  }

  create(update: PortalNotificationUpdate): Observable<number> {
    return this.portalDataService.translationMeta.data$.pipe(
      first(),
      mergeMap(({ defaultLanguage }) =>
        this.utils.routeBasedApiUrl(`/notifications?defaultLanguage=${defaultLanguage}`)
      ),
      mergeMap((url) =>
        this.http.post<void>(url, update, {
          observe: 'response',
        })
      ),
      map((response) => Number(response.headers.get('Location').split('/').at(-1))),
      wrapError('notification-repository:create')
    );
  }

  createTranslation({ notificationId, ...otherOptions }: CreateTranslationOptions): Observable<Upload<void>> {
    const formData = this.getTranslationFormData(otherOptions);

    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/translations`).pipe(
      mergeMap((url) =>
        this.uploadService.post<void>({
          formData,
          url,
        })
      ),
      wrapError('notification-repository:create-translation')
    );
  }

  delete(notificationId: number): Observable<void> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('notification-repository:delete')
    );
  }

  deleteMyNotification(notificationId: number): Observable<void> {
    return this.http
      .delete<void>(apiUrl(`/me/notifications/${notificationId}`))
      .pipe(wrapError('portal-notification-repository:delete-my-notification'));
  }

  deleteTranslation(notificationId: number, translationId: number): Observable<void> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/translations/${translationId}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('notification-repository:delete-translation')
    );
  }

  deleteTranslationsByLanguage(notificationId: number, language: string): Observable<void> {
    return this.utils
      .routeBasedApiUrl(`/notifications/${notificationId}/translations?language=${language}`)
      .pipe(
        mergeMap((url) => this.http.delete<void>(url)),
        wrapError('portal-notification-repository:delete-translations-by-language')
      );
  }

  expire(notificationId: number, date?: Date): Observable<void> {
    let params = new HttpParams();

    if (date != null) {
      params = params.append('expiresAt', date.getTime());
    }

    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/expire`).pipe(
      mergeMap((url) =>
        this.http.put<void>(url, null, {
          params,
        })
      ),
      wrapError('portal-notification-repository:expire')
    );
  }

  get(notificationId: number): Observable<PortalNotificationWithTranslations> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}`).pipe(
      mergeMap((url) => this.http.get<PortalNotificationWithTranslations>(url)),
      wrapError('notification-repository:get')
    );
  }

  list(userLevel?: UserLevel): Observable<PortalNotification[]> {
    return this.utils.routeBasedApiUrl('/notifications', userLevel).pipe(
      mergeMap((url) => this.http.get<PortalNotification[]>(url)),
      wrapError('notification-repository:list')
    );
  }

  listAllMyNotifications(): Observable<MyPortalNotification[]> {
    return this.listMyNotifications(false);
  }

  listMyUnseenNotifications(): Observable<MyPortalNotification[]> {
    return this.listMyNotifications(true);
  }

  listRecipients(notificationId: number): Observable<PortalNotificationEnhancedRecipient[]> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/recipients`).pipe(
      mergeMap((url) => this.http.get<PortalNotificationEnhancedRecipient[]>(url)),
      wrapError('portal-notification-repository:list-recipients')
    );
  }

  listTranslations(notificationId: number): Observable<PortalNotificationTranslation[]> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/translations`).pipe(
      mergeMap((url) => this.http.get<PortalNotificationTranslation[]>(url)),
      wrapError('notification-repository:list-translations')
    );
  }

  markAllAsSeen(): Observable<void> {
    return this.http
      .put<void>(apiUrl(`/me/notifications/markSeen/all`), null)
      .pipe(wrapError('portal-notification-repository:mark-all-as-seen'));
  }

  markAsSeen(notificationId: number): Observable<void> {
    return this.http
      .put<void>(apiUrl(`/me/notifications/markSeen/${notificationId}`), null)
      .pipe(wrapError('portal-notification-repository:mark-as-seen'));
  }

  publish(notificationId: number, publicationDate: Date | string): Observable<void> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/publish`).pipe(
      mergeMap((url) =>
        this.http.put<void>(url, {
          publishedAt: typeof publicationDate === 'string' ? publicationDate : formatISO(publicationDate),
        })
      ),
      wrapError('portal-notification-repository:publish')
    );
  }

  render(notificationId: number, language: string): Observable<RenderedPortalNotification> {
    const params = new HttpParams().set('language', language);

    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/render`).pipe(
      mergeMap((url) =>
        this.http.get<RenderedPortalNotification>(url, {
          params,
        })
      ),
      wrapError('portal-notification-repository:render')
    );
  }

  sendTestMail(notificationId: number, data: PortalNotificationSendTestMail): Observable<void> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/sendTestMail`).pipe(
      mergeMap((url) => this.http.put<void>(url, data)),
      wrapError('portal-notification-repository:send-test-mail')
    );
  }

  unpublish(notificationId: number): Observable<void> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/unpublish`).pipe(
      mergeMap((url) => this.http.put<void>(url, undefined)),
      wrapError('portal-notification-repository:unpublish')
    );
  }

  update(notificationId: number, update: PortalNotificationUpdate): Observable<void> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}`).pipe(
      mergeMap((url) => this.http.put<void>(url, update)),
      wrapError('notification-repository:update')
    );
  }

  updatePublishedNotification(
    notificationId: number,
    update: PortalPublishedNotificationUpdate
  ): Observable<void> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/published`).pipe(
      mergeMap((url) => this.http.put<void>(url, update)),
      wrapError('notification-repository:update')
    );
  }

  updateTranslation({
    notificationId,
    translationId,
    ...otherOptions
  }: UpdateTranslationOptions): Observable<Upload<void>> {
    const formData = this.getTranslationFormData(otherOptions);

    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/translations/${translationId}`).pipe(
      mergeMap((url) =>
        this.uploadService.put<void>({
          formData,
          url,
        })
      )
    );
  }

  updateTranslationOrder(
    notificationId: number,
    update: PortalNotificationTranslationOrderUpdate
  ): Observable<void> {
    return this.utils.routeBasedApiUrl(`/notifications/${notificationId}/updateTranslationOrder`).pipe(
      mergeMap((url) => this.http.put<void>(url, update)),
      wrapError('portal-notification-repository:update-translation-order')
    );
  }

  private getTranslationFormData({ file, update }: TranslationOptions): FormData {
    const formData = new FormData();

    formData.append('update', JSON.stringify(update));

    if (file != null) {
      formData.append('media', file, file.name);
    }

    return formData;
  }

  private listMyNotifications(onlyUnseen: boolean): Observable<MyPortalNotification[]> {
    const httpParams = new HttpParams({
      fromObject: {
        onlyUnseen,
      },
    });

    return this.http
      .get<MyPortalNotification[]>(apiUrl('/me/notifications'), {
        params: httpParams,
      })
      .pipe(wrapError('portal-notification-repository:list-my-unseen-notifications'));
  }
}

interface TranslationOptions {
  file?: File;
  update: PortalNotificationTranslationUpdate;
}

export interface CreateTranslationOptions extends TranslationOptions {
  notificationId: number;
}

export interface UpdateTranslationOptions extends TranslationOptions {
  notificationId: number;
  translationId: number;
}
